import React, { useState, useEffect, Fragment } from 'react';
import queryString from 'query-string';
import { BaseSelect } from 'ui_system/forms/BaseSelect';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import styled from '@emotion/styled';
import { colors } from 'ui_system/compass';
import { Theme } from 'ui_system/theme';
import { LocationSearch } from 'ui_system/select/location/LocationSearch';
import { ReactModal } from 'ui_system/modals/ReactModal';

import { t } from 'services/translation';
import ReactLoading from 'react-loading';
import LocationIcon from 'views/ui/icons/location-icon';

import { Api } from './locationApi';
import { LocationInfo, StoresWithin } from './shared';

const StyledLocationIcon = styled(LocationIcon)`
  height: 20px;
  vertical-align: middle;
  margin-right: 8px;
  fill: ${colors.text.icon};
  ${window.mobileDetect.isIE && 'display: none;'}
`;

// https://codepen.io/lajlev/pen/diKbz
const Checkmark = styled.div`
  &:after {
    content: '';
    display: block;
    width: 6px;
    height: 10px;
    border: solid ${colors.text.primary};
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const ChangeLocation = styled.button`
  background: none;
  border: none;
  color: ${colors.text.primary};
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
`;

const Option = styled.div`
  display: flex;
  padding: 2px 5px;
`;

const CheckmarkArea = styled.div`
  width: 18px;
  padding-top: 2px;
`;

const FirstOption = styled.div`
  padding-top: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid ${colors.border};
`;

const Flex = styled.div`
  display: flex;
  align-items: center;
`;

const LoadingText = styled.div`
  margin-right: 10px;
  white-space: nowrap;
`;

const SelectAnOption = styled.div`
  color: ${colors.text.primary};
  white-space: nowrap;
`;

const LocationSearchModal = styled(ReactModal(LocationSearch))`
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    padding: 0px;
    padding-bottom: 50px;
    width: 720px;
  }
`;

function CustomOptionWithUnits(units) {
  return function CustomOption(props) {
    const { data } = props;
    const { value, ...otherData } = data;

    const renderOption = () => {
      if (value) {
        return (
          <Option>
            <CheckmarkArea>
              {props.isSelected && (
                <Checkmark className="brand-border LocationSelect-Option-Checkmark" />
              )}
            </CheckmarkArea>
            <LocationInfo {...value} units={units} />
          </Option>
        );
      }
      // Render first option
      return (
        <FirstOption>
          <StoresWithin {...otherData} units={units} />
          <div data-testid="change-location">
            <ChangeLocation className="brand-color LocationSelect-FirstOption-ChangeLocation">
              {t('CHANGE_LOCATION')}
            </ChangeLocation>
          </div>
        </FirstOption>
      );
    };

    return <components.Option {...props}>{renderOption()}</components.Option>;
  };
}

function CustomSingleValue(props) {
  const { children } = props;
  return (
    <components.SingleValue {...props}>
      <StyledLocationIcon />
      {children}
    </components.SingleValue>
  );
}

function Placeholder({ isGeolocating }) {
  return (
    <Flex>
      <StyledLocationIcon />
      {isGeolocating ? (
        <Flex>
          <LoadingText>{t('FINDING_STORES_NEARBY')}</LoadingText>
          <ReactLoading
            type="spin"
            delay={0}
            height={25}
            width={25}
            color={Theme.colors.lightBorder}
          />
        </Flex>
      ) : (
        <SelectAnOption>{t('SELECT_A_STORE')}</SelectAnOption>
      )}
    </Flex>
  );
}

const valueObj = (location) => ({
  value: location,
  label: location.shopping_center || location.address || location.city,
});

const baseStyles = {
  control: () => ({ fontSize: 18 }),
  // Custom padding for first option:
  option: (state) => (state.data.value ? {} : { padding: '0px 15px' }),
  menuList: () => ({ maxHeight: '360px' }),
};

function getSavedZip() {
  const searchParams = queryString.parse(window.location.href.split('?')[1]);
  if (searchParams.locationQuery) {
    return searchParams.locationQuery;
  }

  const savedZip = localStorage.getItem('savedZip');
  const zipSavedAt = localStorage.getItem('zipSavedAt');

  // If the saved zip hasn't timed out (20 min timeout)
  return zipSavedAt && Date.now() - zipSavedAt < 1000 * 60 * 20 ? savedZip : '';
}

const distanceOptions = [25, 50, 100];

function getDistance() {
  const searchParams = queryString.parse(window.location.href.split('?')[1]);
  const radius = parseInt(searchParams.locationRadius);
  if (radius) {
    const foundOption = distanceOptions.find((val) => val === radius);
    if (foundOption) {
      return foundOption;
    }
  }

  return distanceOptions[0];
}

export function LocationSelect(props) {
  const { value, setValue, userId, tourId, pluginSlug, styles, isDisabled } =
    props;

  const [locations, setLocations] = useState([]);
  const [units, setUnits] = useState('kilometers');
  const [distance, setDistance] = useState(getDistance());
  const [zipCode, setZipCode] = useState(getSavedZip());
  const [modalOpen, setModalOpen] = useState(false);

  const [isGeolocating, setIsGeolocating] = useState(true);

  function _setZipCode(zip) {
    setZipCode(zip);
    localStorage.setItem('savedZip', zip);
    localStorage.setItem('zipSavedAt', Date.now());
  }

  function _setLocations(newLocations) {
    if (newLocations.length > 0) {
      setLocations(newLocations.map((loc) => ({ value: loc })));

      if (value && value.value) {
        const foundLoc = newLocations.find(
          (loc) => value.value.address === loc.address
        );
        if (foundLoc) setValue(valueObj(foundLoc));
      } else {
        const closestLocation = newLocations[0];
        setValue(valueObj(closestLocation));
      }
    }
  }

  useEffect(() => {
    async function fetchZipAndLocations(coords = {}) {
      // Hit api to find zip / locations
      const { latitude, longitude } = coords;

      const params = {
        lat: latitude,
        long: longitude,
        radius: distance,
        user_id: userId,
        tour_id: tourId,
        plugin_slug: pluginSlug,
      };

      try {
        const response = await Api.latLongSearch(params);
        const {
          zip_code: newZipCode,
          units: newUnits,
          locations: newLocations,
        } = response.data;

        setUnits(newUnits);
        _setLocations(newLocations);
        if (newZipCode) {
          _setZipCode(newZipCode);
        }
      } catch (err) {
        //
      }
      setIsGeolocating(false);
    }

    const options = {
      enableHighAccuracy: false,
      timeout: 5000,
    };

    function success(position) {
      const { coords } = position;
      fetchZipAndLocations(coords);
    }

    function error() {
      // Try to fetch based on request IP instead:
      fetchZipAndLocations();
    }

    // Just fetch locations if they've saved a search or have params in the URL (coming from new plugin list)
    async function fetchLocations() {
      const params = {
        query: zipCode,
        radius: distance,
        user_id: userId,
        tour_id: tourId,
        plugin_slug: pluginSlug,
      };

      try {
        const response = await Api.querySearch(params);
        const { locations: newLocations, units: newUnits } = response.data;

        setUnits(newUnits);
        _setLocations(newLocations);
      } catch (err) {
        setLocations([]);
      }
      setIsGeolocating(false);
    }

    if (zipCode) {
      fetchLocations();
    } else {
      navigator.geolocation.getCurrentPosition(success, error, options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Open change location modal
  function openSearch() {
    setModalOpen(true);
  }

  const otherProps = {};
  if (!isGeolocating && locations.length === 0) {
    otherProps.onMenuOpen = () => openSearch();
    otherProps.menuIsOpen = false;
  }

  function onChange(location) {
    const locationValue = location.value;
    if (locationValue) {
      setValue(valueObj(locationValue));
    } else {
      openSearch();
    }
  }

  return (
    <Fragment>
      <LocationSearchModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        zipCode={zipCode}
        locations={locations}
        distance={distance}
        units={units}
        distanceOptions={distanceOptions}
        userId={userId}
        tourId={tourId}
        pluginSlug={pluginSlug}
        onSelect={(val) => {
          setDistance(val.distance);
          _setZipCode(val.zipCode);
          setLocations(val.locations);
          setValue(val.location);
          setModalOpen(false);
        }}
      />
      <BaseSelect
        value={isGeolocating ? null : value}
        placeholder={<Placeholder isGeolocating={isGeolocating} />}
        isDisabled={isDisabled || isGeolocating}
        onChange={onChange}
        options={[{ numStores: locations.length, distance, zipCode }].concat(
          locations
        )}
        components={{
          SingleValue: CustomSingleValue,
          Option: CustomOptionWithUnits(units),
        }}
        styles={{ ...baseStyles, ...styles }}
        {...otherProps}
      />
    </Fragment>
  );
}

LocationSelect.propTypes = {
  value: PropTypes.object,
  setValue: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
  tourId: PropTypes.number,
  pluginSlug: PropTypes.string,
  styles: PropTypes.object,
};
